import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Download, Upload, X } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { apiRequest, downloadApiFile, getApiErrorMessage } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import type {
  Organization,
  ProductImportBatch,
  ProductImportRow,
} from '../../types/domain';

function issueText(row: ProductImportRow) {
  if (Array.isArray(row.errors)) return row.errors.join('; ') || 'Ready to import';
  return Object.entries(row.errors)
    .flatMap(([field, messages]) => messages.map((message) => `${field}: ${message}`))
    .join('; ') || 'Ready to import';
}

export function ProductImportPanel({
  accessToken,
  clients,
  initialClient,
  onClose,
}: {
  accessToken: string | null;
  clients: Organization[];
  initialClient: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [client, setClient] = useState(initialClient);
  const [file, setFile] = useState<File | null>(null);
  const [batch, setBatch] = useState<ProductImportBatch | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const rows = useApiCollection<ProductImportRow>(
    'product-import-rows',
    `/product-imports/${batch?.id}/rows/?page_size=1000`,
    accessToken,
    Boolean(batch),
  );
  const visibleRows = rows.data?.results ?? [];
  const importable = visibleRows.filter((row) => (
    row.classification === 'valid_new' || row.classification === 'valid_update'
  ));

  const preview = useMutation({
    mutationFn: () => {
      if (!file || !client) throw new Error('Select a client and CSV file.');
      const body = new FormData();
      body.append('client', client);
      body.append('file', file);
      return apiRequest<ProductImportBatch>('/product-imports/preview/', {
        ...(accessToken ? { accessToken } : {}), method: 'POST', body,
      });
    },
    onSuccess: (result) => { setBatch(result); setSelectedRows(new Set()); },
  });
  const confirm = useMutation({
    mutationFn: () => apiRequest<ProductImportBatch>(`/product-imports/${batch?.id}/confirm/`, {
      ...(accessToken ? { accessToken } : {}),
      method: 'POST',
      body: JSON.stringify({ rows: Array.from(selectedRows) }),
    }),
    onSuccess: async (result) => {
      setBatch(result);
      await queryClient.invalidateQueries({ queryKey: ['collection', 'products'] });
    },
  });
  const template = useMutation({
    mutationFn: () => downloadApiFile('/product-imports/template/', accessToken ? { accessToken } : {}),
  });

  function toggle(rowId: string) {
    setSelectedRows((current) => {
      const next = new Set(current);
      if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
      return next;
    });
  }

  return <div className="confirmation-backdrop" role="presentation">
    <section aria-labelledby="product-import-title" aria-modal="true" className="confirmation-dialog product-import-dialog" role="dialog">
      <button aria-label="Close product import" className="icon-button confirmation-close" onClick={onClose} type="button"><X size={19} /></button>
      <div className="card-heading"><div><h2 id="product-import-title">Bulk product import</h2><p>Download the controlled template, preview every row, then confirm only reviewed rows.</p></div></div>
      {!batch ? <>
        <button className="button secondary" disabled={template.isPending} onClick={() => template.mutate()} type="button"><Download size={16} /> Download CSV template</button>
        <form className="management-form product-import-upload" onSubmit={(event: FormEvent) => { event.preventDefault(); preview.mutate(); }}>
          <div className="form-grid">
            <label>Client<select required value={client} onChange={(event) => setClient(event.target.value)}><option value="">Select client</option>{clients.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
            <label>Product CSV<input accept=".csv,text/csv" required type="file" onChange={(event) => setFile(event.target.files?.[0] ?? null)} /></label>
          </div>
          <div className="form-actions"><button className="button primary" disabled={!client || !file || preview.isPending} type="submit">{preview.isPending ? <InlineSpinner /> : <Upload size={16} />} Preview import</button></div>
        </form>
      </> : <>
        <div className="venue-import-meta"><div><span>Preview</span><strong>{batch.reference}</strong><small>{batch.client_name} · {batch.original_filename}</small></div><StatusPill value={batch.status} /></div>
        <div className="venue-import-summary"><div><span>Total</span><strong>{batch.total_rows}</strong></div><div><span>New</span><strong>{batch.valid_new_count}</strong></div><div><span>Updates</span><strong>{batch.valid_update_count}</strong></div><div><span>Invalid / conflicts</span><strong>{batch.invalid_count + batch.conflict_count}</strong></div></div>
        {batch.status === 'confirmed' ? <div className="success-banner"><Check size={18} /> Imported {batch.imported_count} reviewed rows.</div> : <>
          <div className="table-actions"><button className="button secondary" onClick={() => setSelectedRows(new Set(importable.map((row) => row.id)))} type="button">Select all eligible</button></div>
          <DataPanel loading={rows.isLoading} error={rows.isError} empty={visibleRows.length === 0}>
            <div className="data-table-wrap"><table className="data-table"><thead><tr><th><span className="sr-only">Select</span></th><th>Row</th><th>Product</th><th>Identifiers</th><th>Result</th><th>Review detail</th></tr></thead><tbody>{visibleRows.map((row) => {
              const canSelect = row.classification === 'valid_new' || row.classification === 'valid_update';
              return <tr key={row.id}><td><input aria-label={`Select product row ${row.row_number}`} checked={selectedRows.has(row.id)} disabled={!canSelect} onChange={() => toggle(row.id)} type="checkbox" /></td><td>{row.row_number}</td><td><strong>{row.data.source.name || 'Missing name'}</strong><small>{row.data.source.variant_label}</small></td><td>{row.data.source.sku || 'No SKU'}<small>{row.data.source.barcode_gtin || 'No barcode'}</small></td><td><StatusPill value={row.classification} /></td><td>{issueText(row)}</td></tr>;
            })}</tbody></table></div>
          </DataPanel>
          <div className="venue-import-confirm"><div><strong>{selectedRows.size} rows selected</strong><p>Confirmation is atomic and writes nothing until this step.</p></div><button className="button primary" disabled={!selectedRows.size || confirm.isPending} onClick={() => confirm.mutate()} type="button">{confirm.isPending ? <InlineSpinner /> : <Check size={16} />} Confirm selected products</button></div>
        </>}
      </>}
      {template.isError || preview.isError || confirm.isError ? <p className="form-error" role="alert">{getApiErrorMessage(template.error ?? preview.error ?? confirm.error)}</p> : null}
    </section>
  </div>;
}
